<script setup>
import { ref, shallowRef } from 'vue';
import UiFormDialog from '@/components/ui/UiFormDialog.vue';
import UiTableCard from '@/components/ui/UiTableCard.vue';
import { apiRequest } from '@/api/http';
import { useAsyncAction } from '@/composables/useAsyncAction';
import { useConfirm } from '@/composables/useConfirm';
import { useListResource } from '@/composables/useListResource';
import { useSessionGuard } from '@/composables/useSessionGuard';

useSessionGuard();

// Senior High strands and the track each belongs to.
const headers = [
  { title: 'Strand', key: 'strand_name' },
  { title: 'Track', key: 'tracks_name' },
];
const { items, loading, error, load } = useListResource(() =>
  apiRequest('get', '/rooms-section/AllStrand/Data/strand'),
);
load();

const tracks = shallowRef([]);
apiRequest('get', '/rooms-section/getAlltracks/Data/tracks')
  .then((rows) => (tracks.value = Array.isArray(rows) ? rows : []))
  .catch(() => {});

const editing = shallowRef(null); // null = add
const formOpen = shallowRef(false);
const form = ref({ name: '', trackId: null });
const fields = [
  { key: 'name', label: 'Strand name', required: true, placeholder: 'e.g. STEM' },
  { key: 'trackId', label: 'Track', type: 'autocomplete', required: true, items: tracks, itemTitle: 'tracks_name', itemValue: 'id' },
];
const { busy, run } = useAsyncAction();
const { confirm } = useConfirm();

function openForm(strand = null) {
  editing.value = strand;
  form.value = {
    name: strand?.strand_name ?? '',
    trackId: strand?.trackId != null ? Number(strand.trackId) : null,
  };
  formOpen.value = true;
}

async function save() {
  const data = { strand_name: form.value.name.trim(), trackId: form.value.trackId };
  const ok = await run(
    () =>
      editing.value
        ? apiRequest('patch', `/rooms-section/updateStrand/${editing.value.id}`, data)
        : apiRequest('post', '/rooms-section/addStrand', data),
    { success: editing.value ? 'Strand updated.' : 'Strand added.', error: 'Could not save the strand.' },
  );
  if (ok) {
    formOpen.value = false;
    load();
  }
}

async function archive(strand) {
  const ok = await confirm({
    title: 'Archive strand?',
    description: `${strand.strand_name} will no longer be available for new sections.`,
    confirmText: 'Archive',
    destructive: true,
  });
  if (ok && (await run(() => apiRequest('patch', `/rooms-section/archieveStrand/${strand.id}`, {}), { success: 'Strand archived.', error: 'Could not archive the strand.' }))) {
    load();
  }
}
</script>

<template>
  <UiTableCard
    title="Strands"
    description="Senior High strands and the track each belongs to."
    :headers="headers"
    :items="items"
    :loading="loading"
    :error="error"
    search-placeholder="Search strands"
    add-label="Add strand"
    empty-text="No strands yet."
    row-label="strand_name"
    :row-actions="(strand) => [
      { title: 'Edit', icon: 'mdi-pencil-outline', onClick: () => openForm(strand) },
      { title: 'Archive', icon: 'mdi-archive-outline', destructive: true, onClick: () => archive(strand) },
    ]"
    @add="openForm()"
  >
    <template #[`item.strand_name`]="{ item }">
      <span class="font-weight-medium">{{ item.strand_name }}</span>
    </template>
  </UiTableCard>

  <UiFormDialog
    v-model="formOpen"
    :title="editing ? 'Edit strand' : 'Add strand'"
    v-model:values="form"
    :fields="fields"
    :saving="busy"
    :submit-text="editing ? 'Save changes' : 'Add strand'"
    @submit="save"
  />
</template>
